import type { LocaleResource } from './types';
import { getA11yFallback } from './a11yFallback';

const resource = {
  common: {
    loading: 'Загрузка...',
    actions: {
      close: 'Закрыть меню',
      search: 'Поиск',
      backHome: 'Вернуться на главную',
      login: 'Войти',
      register: 'Создать аккаунт',
      explore: 'Исследовать',
    },
    states: {
      noResults: 'Нет результатов',
      noResultsFor: 'Нет результатов для “{{query}}”.',
    },
  },
  app: {
    skipToContent: 'Перейти к основному содержимому',
    loading: 'Загрузка...',
    notFound: {
      message: 'Эта страница не существует во вселенной Skoleom.',
      cta: 'Вернуться на главную',
    },
  },
  a11y: getA11yFallback('ru'),
  header: {
    brandHome: 'Главная',
    universe: 'Universe',
    navLabel: 'Основная навигация',
    searchPlaceholder: 'Поиск',
    searchAria: 'Поиск',
    language: {
      change: 'Сменить язык',
      zones: {
        international: 'Международный',
        europe: 'Европа и Великобритания',
        americas: 'Америка',
        mena: 'MENA',
        asiaPacific: 'Азиатско-Тихоокеанский регион',
        africa: 'Африка',
      },
    },
    actions: {
      settings: 'Настройки',
      favorites: 'Избранное',
      cart: 'Корзина',
      account: 'Аккаунт пользователя',
      openMenu: 'Открыть меню',
      closeMenu: 'Закрыть меню',
    },
    account: {
      dashboard: 'Моя панель',
      orders: 'Мои заказы',
      favorites: 'Избранное',
      admin: 'Панель администратора',
      adminBadge: 'Admin',
      logout: 'Выйти',
    },
    nav: {
      home: 'Главная',
      stores: 'Магазины',
      music: 'Музыка',
      subscriptions: 'Подписки',
      everyone: 'Для всех',
      pros: 'Для профессионалов',
      news: 'Новости',
      events: 'События',
      group: 'Группа',
      support: 'Поддержка',
    },
  },
  cart: {
    title: 'Моя корзина',
    itemCount: '{{count}} товар(ов)',
    empty: {
      title: 'Ваша корзина пуста',
      subtitle: 'Откройте для себя наш покупаемый контент',
    },
    summary: {
      subtotal: 'Промежуточный итог',
      shipping: 'Доставка',
      free: 'Бесплатно',
      total: 'Итого',
    },
    checkout: {
      cta: 'Оплатить через Skoleom Pay',
      security: 'Суверенный и безопасный платеж · соответствует GDPR',
    },
    aria: {
      decrease: 'Уменьшить количество',
      increase: 'Увеличить количество',
      remove: 'Удалить этот товар',
      close: 'Закрыть корзину',
    },
  },
  skoleomPage: {
    nav: {
      ariaLabel: 'Навигация Skoleom Page',
      home: 'Главная',
      homeMobile: 'Главная',
      trends: 'Тренды',
      trendsMobile: 'Тренды',
      create: 'Создайте SeContent',
      createMobile: 'Создать',
      profile: 'Профиль',
      profileMobile: 'Профиль',
    },
    explorer: {
      poweredBy: 'На базе SeSync',
      heroBefore: 'Смотрите. Нажимайте. ',
      heroHighlight: 'Открывайте.',
      heroSubtitle:
        'Один поиск — вся экосистема Skoleom Universe: видео, музыка, медиа, покупки. Каждый результат — иммерсивный вход.',
      logoAlt: 'Skoleom Page',
      searchPlaceholder: 'Поиск артиста, фильма, бренда…',
      searchAria: 'Поисковый запрос',
      searchSubmit: 'Начать поиск',
      searchReset: 'Сбросить поиск',
      exploreCta: 'Исследовать',
      resultsFor: 'Результаты для',
      resultsOnPlatforms_one: 'на {{count}} платформе',
      resultsOnPlatforms_other: 'на {{count}} платформах',
      filter: 'Фильтр',
      platforms: 'Платформы',
      filterPlatformsAria: 'Фильтровать платформы',
      selectAll: 'Все',
      selectNone: 'Ничего',
      loading: 'Подключение к вселенной Skoleom…',
      noResults: 'Нет результатов по текущим фильтрам. Выберите больше платформ.',
    },
    actionCards: {
      sectionBefore: 'Откройте ',
      sectionHighlight: 'вселенную Skoleom',
      mobile: {
        title: 'Мобильный',
        description: 'Доступно на iOS и Android.',
      },
      tv: {
        title: 'ТВ',
        description: 'Смотрите на подключённом ТВ.',
      },
      rewards: {
        title: 'Награды',
        description: 'Кэшбэк на премиум-покупки.',
      },
      extension: {
        title: 'Расширение',
        description: 'Доступ ко всем функциям.',
      },
      audio: {
        title: 'Аудио',
        description: 'Слушайте на всех устройствах.',
      },
      gaming: {
        title: 'Игры',
        description: 'Откройте наши игровые приложения.',
      },
    },
  },
  footer: {
    brand: {
      description:
        'Экосистема Skoleom объединяет аудиовизуальные магазины, инструменты для создателей и бизнес-решения, чтобы делать контент интерактивным и действенным.',
    },
    socials: {
      label: 'Социальные сети Skoleom',
    },
    shopPrompt: {
      prefix: 'Вы также можете делать покупки в',
      link: 'интернет-магазине Skoleom',
    },
    copyright: 'Все права защищены.',
    modal: {
      close: 'Закрыть меню',
      title: 'Сервис недоступен',
      body: 'Этот сервис недоступен в вашем регионе.',
    },
    columns: {
      about: 'О Skoleom',
      stores: 'Официальные аудиовизуальные магазины',
      everyone: 'Для всех',
      pros: 'Для профессионалов',
      group: 'Группа',
      '1': 'О Skoleom',
      '2': 'Аудиовизуальные магазины',
      '3': 'Для всех',
      '4': 'Для профессионалов',
      '5': 'Группа',
    },
    links: {
      '101': 'Наша миссия',
      '102': 'Наша технология',
      '103': 'Экосистема Skoleom',
      '104': 'Программы финансирования',
      '105': 'Патент Skoleom',
      '201': 'Исследовать магазины',
      '202': 'Вселенные по категориям',
      '203': 'Создать магазин',
      '301': 'Как это работает',
      '302': 'Смотреть и покупать',
      '303': 'Кэшбэк и награды',
      '304': 'Skoleom Wallet',
      '305': 'Безопасность и конфиденциальность',
      '401': 'Бизнес-решения',
      '402': 'Монетизировать контент',
      '403': 'Создать аудиовизуальный магазин',
      '404': 'Распространять продукты',
      '405': 'Интерактивная реклама',
      '406': 'API и интеграции',
      '407': 'Создать Audiovisual Store Page',
      '501': 'Новости',
      '502': 'Карьера',
      '503': 'Инвесторы',
      '504': 'Пресса',
      '505': 'Контакты',
      '506': 'Поддержка',
    },
    legalLinks: {
      '901': 'Правовая информация',
      '902': 'Условия использования и продажи',
      '903': 'Конфиденциальность',
      '904': 'Партнерское раскрытие',
      '905': 'Настройки cookie',
      '906': 'Политика возврата',
    },
  },
  auth: {
    login: {
      title: 'Войти',
      submit: 'Войти',
    },
    register: {
      title: 'Создать аккаунт',
      submit: 'Создать аккаунт',
    },
  },
  dashboard: {
    user: {
      orders: 'Мои заказы',
    },
    admin: {
      title: 'Панель администратора',
    },
  },
  home: {
    hero: {
      title: 'Откройте глобальную экосистему Skoleom Group',
      description: {
        intro: 'Skoleom Group революционизирует ',
        economy: 'цифровую экономику',
        mid: ', позволяя мгновенные покупки внутри аудиовизуального контента. Подключена к более чем ',
        ott: '2000 OTT-платформам',
        join: ' и ',
        web: 'миллиардам сайтов',
        outro: ' — наша технология превращает каждый экран в интерактивную точку продаж.',
      },
      download: 'Скачать',
      application: 'приложение',
      extension: 'расширение',
      watchVideo: 'Смотреть видео',
      tagline1: 'КАЖДОЕ ВИДЕО СТАНОВИТСЯ НАСТОЯЩИМ ИММЕРСИВНЫМ МАГАЗИНОМ:',
      tagline2:
        'ПЛАВНЫЙ, ЭЛЕГАНТНЫЙ И ИНТЕРАКТИВНЫЙ ОПЫТ, ГДЕ ВЫ МГНОВЕННО ОТКРЫВАЕТЕ, КАСАЕТЕСЬ И ПОКУПАЕТЕ УВИДЕННОЕ.',
    },
    actions: {
      discoverMagic: 'Откройте магию',
    },
    welcome: {
      part1: 'Добро пожаловать в ',
      part2: 'будущее ',
      part3: 'retail media',
      part4: 'и ',
      part5: 'трансмедиа',
    },
    page: {
      heroClose: 'Закрыть',
      interactiveKicker: 'Интерактивный опыт',
      interactiveSubtitle: 'Внутри видео.\nВ тот же момент.\nБез трения.',
      ecosystemKicker: 'Одна вселенная. Множество возможностей.',
      ecosystemTitle: 'Экосистема\nSkoleom',
      partnersTitle: 'Партнёрские бренды',
      boutiquesCta: 'Откройте наши',
      boutiquesTitle: 'Аудиовизуальные магазины',
      boutiquesSubtitle:
        'Иммерсивная экосистема. Более 50 веб-приложений. Один предел: ваше воображение.',
    },
    news: {
      badge: 'Новинки',
    },
    boutiques: {
      title: 'Откройте наши аудиовизуальные магазины',
      searchPlaceholder: 'Поиск сайта Skoleom (например: market, sport, ott...)',
    },
    sections: {
      welcome: 'Добро пожаловать в будущее retail media и трансмедиа',
      how: 'Как работает Skoleom',
      moment: 'Каждый момент. Становится. Возможностью.',
      interactive: 'Интерактивный опыт',
      universe: 'Одна вселенная. Множество возможностей.',
      ecosystem: 'Экосистема Skoleom',
      partners: 'Партнёрские бренды',
    },
    descriptions: {
      detect:
        'Skoleom распознаёт, что вы смотрите, и делает всё увиденное доступным для покупки — в уникальном интерактивном опыте.',
    },
    stats: {
      storePages: 'страниц Audiovisual Store на магазин.',
    },
  },
  public: {
    stores: {
      hero: {
        titleLine1: 'Откройте наши',
        titleLine2: 'Аудиовизуальные магазины',
        subtitle: 'Иммерсивная экосистема. Более 50 веб-приложений. Один предел: ваше воображение.',
      },
      search: {
        placeholder: 'Поиск магазина или бренда',
        aria: 'Поиск магазина или бренда',
      },
      sections: {
        official: 'Официальные магазины',
        brands: 'Доступные бренды',
      },
      showcase: {
        kicker: 'Избранные магазины',
        title: 'Флагманские вселенные',
        subtitle: 'Спорт, OTT, музыка и гейминг — культовые аудиовизуальные магазины.',
      },
      carousel: {
        kicker: 'Все магазины',
        title: 'Аудиовизуальные магазины',
        subtitle: 'Все магазины экосистемы Skoleom.',
        searchPlaceholder: 'Поиск магазина (напр.: sport, music, games...)',
        ariaLabel: 'Карусель аудиовизуальных магазинов',
      },
    },
    howItWorks: {
      hero: {
        kicker: 'Как это работает',
        subtitle: 'Внутри видео. В тот же момент. Без трения.',
      },
      steps: {
        '01': {
          title: 'РАСПОЗНАВАНИЕ ИИ',
          desc: 'Наш ИИ определяет продукты, объекты и моменты в любом контенте.',
        },
        '02': {
          title: 'УМНЫЕ КАПСУЛЫ',
          desc: 'Интерактивные капсулы появляются в реальном времени без прерывания.',
        },
        '03': {
          title: 'ОДИН ТАП',
          desc: 'Один клик. Без редиректа. Без трения. Мгновенное действие.',
        },
        '04': {
          title: 'ПОКУПКА В КОНТЕКСТЕ',
          desc: 'Завершите покупку, не покидая любимый контент.',
        },
      },
      demo: {
        titlePrefix: 'Откройте',
        titleHighlight: 'магию',
        cta: 'Запустить интерактивное демо',
      },
      showcase: {
        kicker: 'Вселенная для всех',
        title: 'Избранные магазины',
        subtitle: 'Интерактивные experiences для всех, в вашем любимом контенте.',
      },
      carousel: {
        kicker: 'Все магазины',
        title: 'Для всех',
        subtitle: 'Все потребительские магазины экосистемы Skoleom.',
        searchPlaceholder: 'Поиск (напр.: page, shop, magazine...)',
        ariaLabel: 'Карусель магазинов Для всех',
      },
    },
    technology: {
      hero: {
        kicker: 'SeSync · Технология',
        titlePrefix: 'Подключено к более чем',
        titleMiddle: 'и',
      },
      features: {
        seamless: 'Бесшовные покупки прямо в видео',
        compatible: 'Совместимо с любимыми брендами и креаторами',
        secure: 'Безопасно и соответствует GDPR',
      },
      download: 'Скачать расширение',
      studio: {
        titleLine1: 'Инструмент монетизации',
        titleLine2Prefix: 'самый',
        titleLine2Highlight: 'мощный в мире',
        description:
          'ERP/DCM SaaS для управления, монетизации и анализа интерактивного видео. 300+ API. Полный контроль.',
      },
    },
  },
  data: {
    showcase: {
      'av-sesports': {
        title: 'Skoleom SeSports',
        desc: 'Interactive sports universe: video-to-commerce, retail media and fan engagement. The group official audiovisual store for sports brands and federations.',
      },
      'av-watch': {
        title: 'Watch on Skoleom',
        desc: 'Skoleom shoppable OTT experience: movies, series and live streams where every visible product becomes accessible in one tap.',
      },
      'av-music': {
        title: 'Skoleom Music',
        desc: 'Label, artists and merchandising. The audiovisual store dedicated to music with direct purchase capsules in clips and concerts.',
      },
      'av-games': {
        title: 'Skoleom Games',
        desc: 'Gaming universe and in-game monetization. Proprietary IP, studio partnerships and interactive capsules embedded in content.',
      },
      'tous-sesync': {
        title: 'SeSync',
        desc: 'Skoleom synchronization engine: in-page purchasing and real-time multi-platform distribution.',
      },
      'tous-shop': {
        title: 'Skoleom Shop',
        desc: 'The group official marketplace: products, merchandise and limited editions available directly from your favorite content.',
      },
      'tous-secontent': {
        title: 'SeContent Creation',
        desc: 'Creation and orchestration of interactive capsules. Intuitive tools to turn any content into a shoppable experience.',
      },
      'tous-magazine': {
        title: 'Skoleom Magazine',
        desc: 'Media and interactive formats: news, features and reports enriched with inline shopping capsules.',
      },
    },
    boutiques: {
      'av-sesports': {
        name: 'Skoleom SeSports',
        description: 'Sports universe and retail media.',
      },
      'av-cares': {
        name: 'Skoleom Cares',
        description: 'Social impact and dedicated programs.',
      },
      'av-games': {
        name: 'Skoleom Games',
        description: 'Games, IP and in-game monetization.',
      },
      'av-watch': {
        name: 'Watch on Skoleom',
        description: 'Shoppable OTT content and live experiences.',
      },
      'av-music': {
        name: 'Skoleom Music',
        description: 'Music, artists and direct merchandising.',
      },
      'tous-page': {
        name: 'Skoleom Page',
        description: 'Skoleom entry point and discovery.',
      },
      'tous-sesync': {
        name: 'SeSync',
        description: 'In-page purchasing and distribution.',
      },
      'tous-shop': {
        name: 'Skoleom Shop',
        description: 'Official marketplace for the ecosystem.',
      },
      'tous-secontent': {
        name: 'SeContent Creation',
        description: 'Create and orchestrate interactive capsules.',
      },
      'tous-magazine': {
        name: 'Skoleom Magazine',
        description: 'Interactive media, news and reports.',
      },
    },
  },
} satisfies LocaleResource;

export default resource;
